import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { createAccount, getAccountById, getAllAccountsForUser, getAllUpdatesForAccount, updateAccount } from "@/utils/supabase-queries";
import supabaseClient from "@/utils/supabase-browser";
import { Table, UnwrapPromise } from "@/types";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import type { InstagramProfileProps } from "@shared-libs/instagram";
import type { TiktokProfileProps } from "@shared-libs/tiktok";
import { isDemoAccount, fakeMutationDelay, isDemoUser } from "@/utils/demo-helpers";
import { DEMO_USER_ID } from "@/constants";
import { generateDemoAnalytics, generateDemoChartData } from "@/utils/demo-data";

// Profile lookups run server-side (/api/<platform>/get-profile) so the scraper providers,
// the Redis client and their credentials never ship in the browser bundle.
async function fetchProfileViaApi(platform: "instagram" | "tiktok", username: string): Promise<any> {
  const res = await fetch(`/api/${platform}/get-profile?username=${encodeURIComponent(username)}`);
  if (!res.ok) throw new Error(`${platform} profile lookup failed: ${res.status}`);
  const data = await res.json();
  if (data && typeof data === "object" && "error" in data) throw new Error(String(data.error));
  return data;
}

// Avatar URLs point to Supabase storage
const SUPABASE_AVATAR_BASE = "https://db.outfame.com/storage/v1/object/public/avatars";

// Demo accounts fallback data
const DEMO_ACCOUNTS_LIST = [{
  id: "654c6b38-2235-402b-85f9-823e3da306e9",
  user_id: DEMO_USER_ID,
  username: "rhode",
  full_name: "rhode skin",
  avatar_url: `${SUPABASE_AVATAR_BASE}/rhode.jpg`,
  biography: "by @haileybieber ✨",
  followers: 7234567,
  starting_followers: 6650000,
  // ~8.8% growth - makes chart visually impressive
  followings: 50,
  media_count: 580,
  account_type: "pro",
  enabled: true,
  active: true,
  platform: "instagram",
  created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: "c7a8face-cc36-4505-9cb1-78b6a49354a6",
  user_id: DEMO_USER_ID,
  username: "emmachamberlain",
  full_name: "emma chamberlain",
  avatar_url: `${SUPABASE_AVATAR_BASE}/emmachamberlain.jpg`,
  biography: "podcast @anythinggoes ☕️ coffee @chamberlaincoffee",
  followers: 16123456,
  starting_followers: 14800000,
  // ~8.9% growth
  followings: 3,
  media_count: 723,
  account_type: "glory",
  enabled: true,
  active: true,
  platform: "instagram",
  created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: "06986620-54f0-444d-9fff-b92495c92680",
  user_id: DEMO_USER_ID,
  username: "matildadjerf",
  full_name: "Matilda Djerf",
  avatar_url: `${SUPABASE_AVATAR_BASE}/matildadjerf.jpg`,
  biography: "Swedish 🇸🇪 @djerfavenue founder",
  followers: 3287654,
  starting_followers: 3000000,
  // ~9.6% growth
  followings: 1609,
  media_count: 1680,
  account_type: "go",
  enabled: true,
  active: true,
  platform: "instagram",
  created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
}] as unknown as Table<"accounts">[];
export type InitialAccountListType = UnwrapPromise<ReturnType<typeof getAllAccountsForUser>>;
export const useAccountsList = initialAccountList => {
  const $ = _c(6);
  const user = useLoggedInUser();
  const t0 = user?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = ["account-list", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== user?.email) {
    t2 = async () => {
      if (isDemoUser(user?.email)) {
        return DEMO_ACCOUNTS_LIST;
      }
      return getAllAccountsForUser(supabaseClient);
    };
    $[2] = user?.email;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== initialAccountList) {
    t3 = {
      initialData: initialAccountList,
      staleTime: 120000,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    };
    $[4] = initialAccountList;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useQuery(t1, t2, t3);
};
export const useCreateAccountMutation = t0 => {
  const $ = _c(12);
  const {
    onSuccess,
    onSettled,
    onError
  } = t0;
  const user = useLoggedInUser();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== user) {
    t1 = async t2 => {
      const {
        accountUsername,
        accountPlatform,
        themeColor
      } = t2;
      let fetched_account_data;
      console.log("\uD83D\uDE80 ~ file: react-query-hooks.ts:216 ~ accountPlatform", accountPlatform);
      if (accountPlatform === "instagram") {
        console.log(`fetching instagram profile for ${accountUsername}`);
        fetched_account_data = await fetchProfileViaApi("instagram", accountUsername);
      } else {
        if (accountPlatform === "tiktok") {
          console.log(`fetching tiktok profile for ${accountUsername}`);
          fetched_account_data = await fetchProfileViaApi("tiktok", accountUsername);
        }
      }
      console.log("\uD83D\uDE80 ~ file: react-query-hooks.ts:219 ~ fetched_profile_data", fetched_account_data);
      console.log(fetched_account_data);
      if (!fetched_account_data) {
        throw new Error(`This ${accountPlatform === "instagram" ? "Instagram" : "TikTok"} account cannot be connected. Please double-check the username.`);
      }
      ;
      try {
        if (accountPlatform === "instagram") {
          await fetch(`/api/instagram/download-avatar?username=${accountUsername}`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              username: accountUsername,
              avatar_url: fetched_account_data.graphql.user.profile_pic_url
            })
          });
        } else {
          if (accountPlatform === "tiktok") {
            await fetch(`/api/tiktok/download-avatar?username=${accountUsername}`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                username: accountUsername,
                avatar_url: fetched_account_data.profile_pic_url
              })
            });
          }
        }
      } catch (t3) {
        const error = t3;
        console.error(`Error downloading ${accountPlatform} avatar for @${accountUsername}: `, error);
      }
      return createAccount(supabaseClient, user, fetched_account_data, accountPlatform, themeColor, undefined, accountUsername);
    };
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSuccess || $[3] !== queryClient || $[4] !== user?.id) {
    t2 = data => {
      queryClient.invalidateQueries(["account-list", user?.id]);
      queryClient.invalidateQueries(["all-accounts", user?.id]);
      toast.success("Account connected successfully!");
      onSuccess?.(data.id);
    };
    $[2] = onSuccess;
    $[3] = queryClient;
    $[4] = user?.id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onError) {
    t3 = (error_0, variables) => {
      console.error("Error creating account: ", error_0.message);
      if (error_0.message.includes("profile is already connected to an Outfame account.")) {
        toast.error(`This ${variables.accountPlatform === "instagram" ? "Instagram" : "TikTok"} profile is already connected to an Outfame account.`);
      } else {
        toast.error("An error occurred while connecting the account.");
      }
      onError?.(error_0, variables);
    };
    $[6] = onError;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onSettled || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      onMutate: _temp,
      onSuccess: t2,
      onSettled,
      onError: t3
    };
    $[8] = onSettled;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useMutation(t1, t4);
};
export type AccountByIdData = UnwrapPromise<ReturnType<typeof getAccountById>>;
export const useGetAccountById = (accountId, initialAccountData) => {
  const $ = _c(6);
  let t0;
  let t1;
  if ($[0] !== accountId) {
    t0 = ["account", accountId];
    t1 = async () => {
      if (isDemoAccount(accountId)) {
        const demoAccount = DEMO_ACCOUNTS_LIST.find(a => a.id === accountId);
        if (demoAccount) {
          const analytics = generateDemoAnalytics(accountId);
          return {
            ...demoAccount,
            analytics: analytics || null,
            actions: 1250
          } as AccountByIdData;
        }
      }
      const account = await getAccountById(supabaseClient, accountId);
      return account;
    };
    $[0] = accountId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!accountId;
  let t3;
  if ($[3] !== initialAccountData || $[4] !== t2) {
    t3 = {
      initialData: initialAccountData,
      staleTime: 600000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: t2
    };
    $[3] = initialAccountData;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useQuery(t0, t1, t3);
};
export const useGetLastAddedAccountForUser = () => {
  const $ = _c(6);
  const user = useLoggedInUser();
  let t0;
  if ($[0] !== user.id) {
    t0 = ["last-added-account", user.id];
    $[0] = user.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== user.email || $[3] !== user.id) {
    t1 = async () => {
      if (isDemoUser(user?.email)) {
        return DEMO_ACCOUNTS_LIST[0];
      }
      const {
        data
      } = await supabaseClient.from("accounts").select("*").eq("user_id", user.id).order("created_at", {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        return data[0];
      } else {
        return null;
      }
    };
    $[2] = user.email;
    $[3] = user.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useQuery(t0, t1, t2);
};
export const useGetAllAccountsForUser = () => {
  const $ = _c(7);
  const user = useLoggedInUser();
  let t0;
  if ($[0] !== user.id) {
    t0 = ["all-accounts", user.id];
    $[0] = user.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== user.email || $[3] !== user.id) {
    t1 = async () => {
      if (isDemoUser(user?.email)) {
        return DEMO_ACCOUNTS_LIST;
      }
      const {
        data
      } = await supabaseClient.from("accounts").select("*").eq("user_id", user.id).order("created_at", {
        ascending: false
      });
      return data;
    };
    $[2] = user.email;
    $[3] = user.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!user?.id;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      staleTime: 120000,
      cacheTime: 300000,
      enabled: t2,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t0, t1, t3);
};
export const useUpdateAccountMutation = t0 => {
  const $ = _c(27);
  const {
    onSuccess,
    onError,
    accountId,
    hideToast
  } = t0;
  const user = useLoggedInUser();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== accountId) {
    t1 = async t2 => {
      const {
        ...updatedData
      } = t2;
      if (isDemoAccount(accountId)) {
        await fakeMutationDelay();
        return Promise.resolve();
      }
      return updateAccount(supabaseClient, accountId, updatedData);
    };
    $[0] = accountId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== accountId || $[3] !== hideToast || $[4] !== queryClient || $[5] !== user?.id) {
    t2 = async updatedData_0 => {
      await queryClient.cancelQueries(["account", accountId]);
      await queryClient.cancelQueries(["all-accounts", user?.id]);
      const prevAccount = queryClient.getQueryData(["account", accountId]);
      const prevAllAccounts = queryClient.getQueryData(["all-accounts", user?.id]);
      queryClient.setQueryData(["account", accountId], old => old ? {
        ...old,
        ...updatedData_0
      } : old);
      queryClient.setQueryData(["all-accounts", user?.id], old_0 => old_0?.map(a => a.id === accountId ? {
        ...a,
        ...updatedData_0
      } : a) ?? old_0);
      if (!hideToast) {
        toast.loading("Updating account settings...", {
          id: "update-account-settings"
        });
      }
      return {
        prevAccount,
        prevAllAccounts
      };
    };
    $[2] = accountId;
    $[3] = hideToast;
    $[4] = queryClient;
    $[5] = user?.id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== accountId || $[8] !== hideToast || $[9] !== onError || $[10] !== queryClient || $[11] !== user?.id) {
    t3 = (error, variables, context) => {
      if (context?.prevAccount !== undefined) {
        queryClient.setQueryData(["account", accountId], context.prevAccount);
      }
      if (context?.prevAllAccounts !== undefined) {
        queryClient.setQueryData(["all-accounts", user?.id], context.prevAllAccounts);
      }
      if (!hideToast) {
        toast.error("Failed to update account settings", {
          id: "update-account-settings"
        });
      }
      onError?.(error);
    };
    $[7] = accountId;
    $[8] = hideToast;
    $[9] = onError;
    $[10] = queryClient;
    $[11] = user?.id;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== accountId || $[14] !== hideToast || $[15] !== onSuccess || $[16] !== queryClient) {
    t4 = data => {
      if (data) {
        queryClient.setQueryData(["account", accountId], old_1 => old_1 ? {
          ...old_1,
          ...data
        } : data);
      }
      if (!hideToast) {
        toast.success("Successfully updated account settings!", {
          id: "update-account-settings"
        });
      }
      onSuccess?.(data);
    };
    $[13] = accountId;
    $[14] = hideToast;
    $[15] = onSuccess;
    $[16] = queryClient;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== accountId || $[19] !== queryClient || $[20] !== user?.id) {
    t5 = () => {
      queryClient.invalidateQueries(["account", accountId]);
      queryClient.invalidateQueries(["all-accounts", user?.id]);
    };
    $[18] = accountId;
    $[19] = queryClient;
    $[20] = user?.id;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4,
      onSettled: t5
    };
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return useMutation(t1, t6);
};
export type UpdatesByIdData = UnwrapPromise<ReturnType<typeof getAllUpdatesForAccount>>;
export const useGetAllUpdatesForAccount = (accountId, t0) => {
  const $ = _c(6);
  const fetchAllUpdates = t0 === undefined ? false : t0;
  let t1;
  let t2;
  if ($[0] !== accountId || $[1] !== fetchAllUpdates) {
    t1 = ["updates", accountId, fetchAllUpdates];
    t2 = async () => {
      if (isDemoAccount(accountId)) {
        return generateDemoChartData(accountId) as unknown as UpdatesByIdData;
      }
      console.log("Fetching updates:", {
        accountId,
        fetchAllUpdates
      });
      return getAllUpdatesForAccount(supabaseClient, accountId, fetchAllUpdates);
    };
    $[0] = accountId;
    $[1] = fetchAllUpdates;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = !!accountId;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      enabled: t3,
      refetchOnWindowFocus: false,
      staleTime: 3600000,
      cacheTime: 3600000,
      refetchInterval: 3600000
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return useQuery(t1, t2, t4);
};
async function _temp(t0) {
  const {
    accountUsername: accountUsername_0
  } = t0;
  console.log(accountUsername_0);
}